import { useState } from "react";
import { cn } from "@/lib/utils";
import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client";
import AiIcon from "@/assets/ai-icon.svg";
import GreenCheckIcon from "@/assets/green-check.svg";
import { XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUserContext } from "@/contexts/UserContextProvider";
import { CompanyRole, ProjectDetailFragment } from "@/__generated__/graphql";
import { SlAPSHOT_BASE_URL } from "@/env";
import {
  Dialog,
  DialogTitle,
  DialogOverlay,
  DialogClose,
  DialogContent,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  SetupSlapshotProjectMutation,
  SetupSlapshotProjectMutationVariables,
} from "@/__generated__/graphql";
import "@/components/EnableAI/EnableAI.css";

export const SETUP_SLAPSHOT_PROJECT_MUTATIOON = gql`
  mutation SetupSlapshotProject($projectId: SmartID!) {
    enableAiForProject(projectId: $projectId) {
      success
      message
      slapshotProjectId
    }
  }
`;

const EnableAI = () => {
  const { currentUser, role, currentProject } = useUserContext();
  const [showEnableAIModal, setShowEnableAIModal] = useState(false);
  const [showCollaboratorWarnModal, setShowCollaboratorWarnModal] =
    useState<boolean>(false);

  if (
    !currentProject ||
    !currentProject.showEnableAiButton ||
    !currentProject.isAiEnabled ||
    !currentUser?.office?.isAiEnabled ||
    role !== "CLIENT" ||
    (role === CompanyRole.Client &&
      currentUser?.subcontractingUser?.office?.company.profile.isDefault)
  ) {
    return null;
  }

  const isCollaborator = (() => {
    if (!currentUser || !currentProject) return false;
    const isSubscribed = currentProject.subscribedUsers?.some(
      (user) => user.id === currentUser.id,
    );
    const isCreator = currentProject.creator?.id === currentUser.id;
    return isSubscribed || isCreator;
  })();

  const handleClick = () => {
    if (!isCollaborator) {
      setShowCollaboratorWarnModal(true);
    } else if (currentProject.slapshotProjectId) {
      window.open(
        `${SlAPSHOT_BASE_URL}/projects/${currentProject.slapshotProjectId}`,
        "_blank",
      );
    } else {
      setShowEnableAIModal(true);
    }
  };

  return (
    <div>
      <Button
        onClick={handleClick}
        className="enableAIButton rounded-[80px] text-primary-foreground"
      >
        <img src={AiIcon} alt="" className="invert dark:invert-0" />
        {currentProject.slapshotProjectId ? "Use AI" : "Enable AI"}
      </Button>
      {showCollaboratorWarnModal && (
        <CollaboratorWarnModal
          open={showCollaboratorWarnModal}
          onClose={() => setShowCollaboratorWarnModal(false)}
          selectedProject={currentProject}
        />
      )}
      {showEnableAIModal && (
        <EnableAIModal
          open={showEnableAIModal}
          onClose={() => setShowEnableAIModal(false)}
          selectedProject={currentProject}
        />
      )}
    </div>
  );
};

const CollaboratorWarnModal = ({
  open,
  onClose,
  selectedProject,
}: {
  open: boolean;
  onClose: () => void;
  selectedProject: ProjectDetailFragment;
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogOverlay />
      <DialogContent
        className={cn(
          "p-4 rounded-[4px] bg-primary focus:border-none focus:outline-none flex flex-col gap-6",
        )}
      >
        <div className="flex flex-col gap-4">
          <DialogTitle className="text-sm">
            {selectedProject.slapshotProjectId ? "Use AI" : "Enable AI"}
          </DialogTitle>
          <div className="h-px w-full bg-[#2D2D2D]" />
        </div>
        <div className="bg-[#FFAB4D33] rounded-[12px] flex gap-[10px] py-[12px] px-[20px]">
          <p className="text-sm text-secondary-foreground">
            You're not currently a collaborator on this project, so this feature
            isn't available to you. Please reach out to your Production team or
            the Hotspring team to request access.
          </p>
          <DialogClose className="cursor-pointer">
            <XIcon />
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const EnableAIModal = ({
  open,
  onClose,
  selectedProject,
}: {
  open: boolean;
  onClose: () => void;
  selectedProject: ProjectDetailFragment;
}) => {
  const [showSuccessView, setShowSuccessView] = useState(false);
  const [showErrorView, setShowErrorView] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>();

  const [setupSlapshotProject] = useMutation<
    SetupSlapshotProjectMutation,
    SetupSlapshotProjectMutationVariables
  >(SETUP_SLAPSHOT_PROJECT_MUTATIOON);

  if (showSuccessView) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogOverlay />
        <DialogContent
          className={cn(
            "p-4 rounded-[4px] bg-primary focus:border-none focus:outline-none flex flex-col gap-6",
          )}
        >
          <div className="flex flex-col gap-4">
            <DialogTitle className="text-sm">
              {selectedProject.slapshotProjectId ? "Use AI" : "Enable AI"}
            </DialogTitle>
            <div className="h-px w-full bg-[#2D2D2D]" />
          </div>
          <div className="flex flex-col gap-6 flex flex-col justify-center items-center">
            <img src={GreenCheckIcon} alt="" className="h-12 w-12" />
            <div className="flex flex-col gap-4 text-sm text-secondary-foreground">
              <p>
                <span>Woohoo!&nbsp;</span>
                You can now use our VFX AI toolkit on all your shots.
              </p>
              <p>
                You should have received an email to set up your account on our
                AI platform - Slapshot. All your shots will be synced over and
                ready to use shortly.
              </p>
              <p>
                If you already have an account the&nbsp;
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${SlAPSHOT_BASE_URL}/auth/login`}
                  className="text-service-paint font-bold"
                >
                  click here
                </a>
                &nbsp;to get started.
              </p>
            </div>
          </div>
          <DialogFooter>
            <div className="w-full flex flex-col gap-4">
              <div className="h-px w-full bg-[#2D2D2D]" />
              <div className="flex w-full justify-end gap-4">
                <Button
                  className="bg-service-paint rounded-[4px] hover:bg-service-paint"
                  onClick={onClose}
                >
                  Okay! got it
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (showErrorView) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogOverlay />
        <DialogContent
          className={cn(
            "p-4 rounded-[4px] bg-primary focus:border-none focus:outline-none flex flex-col gap-6",
          )}
        >
          <div className="flex flex-col gap-4">
            <DialogTitle className="text-sm">
              {selectedProject.slapshotProjectId ? "Use AI" : "Enable AI"}
            </DialogTitle>
            <div className="h-px w-full bg-[#2D2D2D]" />
          </div>
          <div className="flex flex-col gap-6 flex flex-col justify-center items-center">
            <div className="flex flex-col gap-4 text-sm text-secondary-foreground">
              <p>Failed to enable AI for this project: {errorMessage}</p>
            </div>
          </div>
          <DialogFooter>
            <div className="w-full flex flex-col gap-4">
              <div className="h-px w-full bg-[#2D2D2D]" />
              <div className="flex w-full justify-end gap-4">
                <Button
                  className="bg-service-paint rounded-[4px] hover:bg-service-paint"
                  onClick={onClose}
                >
                  Ok
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogOverlay />
      <DialogContent
        className={cn(
          "p-4 rounded-[4px] bg-primary focus:border-none focus:outline-none flex flex-col gap-6",
        )}
      >
        <div className="flex flex-col gap-4">
          <DialogTitle className="text-sm">
            {selectedProject.slapshotProjectId ? "Use AI" : "Enable AI"}
          </DialogTitle>
          <div className="h-px w-full bg-[#2D2D2D]" />
        </div>
        <div className="flex flex-col gap-6 flex flex-col justify-center items-center">
          <div className="flex flex-col gap-4 text-sm text-secondary-foreground">
            <p>
              You are now going to Enable AI on Project:&nbsp;
              <span className="text-primary-foreground">
                {selectedProject?.name}
              </span>
            </p>
            <p>
              This action will allow you and your team members to use the latest
              VFX AI toolkit on all shots.
            </p>
            <p>Click the Confirm button below to get started.</p>
          </div>
        </div>
        <DialogFooter>
          <div className="w-full flex flex-col gap-4">
            <div className="h-px w-full bg-[#2D2D2D]" />
            <div className="flex w-full justify-end gap-4">
              <DialogClose className="cursor-pointer">Cancel</DialogClose>
              <Button
                className="bg-service-paint rounded-[4px] hover:bg-service-paint"
                onClick={async () => {
                  const result = await setupSlapshotProject({
                    variables: {
                      projectId: selectedProject.id,
                    },
                  });
                  if (result && result.data && result.data.enableAiForProject) {
                    const { success, message, slapshotProjectId } =
                      result.data.enableAiForProject;
                    if (success && slapshotProjectId) {
                      setShowSuccessView(true);

                      // setSelectedProjectSlapshotProjectId(slapshotProjectId);
                    } else {
                      setErrorMessage(message);
                      setShowErrorView(true);
                    }
                  }
                }}
              >
                Confirm
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EnableAI;
